import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  Renderer2,
  computed,
  effect,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { filter } from 'rxjs';

import { AnnouncementsComponent } from '@shared/components';
import { AnnouncementDisplayService } from '@shared/services';

import { AcceptTermsAndConditionsComponent } from './components/accept-terms-and-conditions/accept-terms-and-conditions.component';
import { AdditionalSideMenuComponent } from './components/additional-side-menu/additional-side-menu.component';
import { AppMenuComponent } from './components/app-menu/app-menu.component';
import { NoSubscriptionOverlayComponent } from './components/no-subscription-overlay/no-subscription-overlay.component';
import { LayoutService } from './services/app-layout.service';

@Component({
  selector: 'app-layout',
  templateUrl: './app-layout.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [AnnouncementDisplayService],
  imports: [
    AcceptTermsAndConditionsComponent,
    NgClass,
    AppMenuComponent,
    RouterModule,
    NoSubscriptionOverlayComponent,
    AdditionalSideMenuComponent,
    AnnouncementsComponent,
  ],
})
export class AppLayoutComponent implements OnDestroy {
  private readonly appMenu = viewChild.required(AppMenuComponent, { read: ElementRef });
  private menuOutsideClickListener?: () => void;

  readonly containerClass = computed(() => {
    const menuMode = this.layoutService.config().menuMode;
    return {
      'layout-overlay': menuMode === 'overlay',
      'layout-static': menuMode === 'static',
      'layout-menu-collapsed': this.layoutService.isMenuCollapsed(),
      'layout-overlay-active': this.layoutService.isOverlayMenuActive(),
      'layout-mobile-active': this.layoutService.isMobileMenuActive(),
    };
  });

  constructor(
    private readonly renderer: Renderer2,
    router: Router,
    readonly layoutService: LayoutService
  ) {
    this.layoutService.overlayOpen$.pipe(takeUntilDestroyed()).subscribe(() => {
      this.menuOutsideClickListener ??= this.renderer.listen('document', 'click', (event: MouseEvent) => {
        // INFO (Denis) - the menu also holds the burger button, whose opening click reaches this listener too
        if (!this.appMenu().nativeElement.contains(event.target as Node)) {
          this.hideMenu();
        }
      });
    });

    effect(() => document.body.classList.toggle('blocked-scroll', this.layoutService.isMobileMenuActive()));

    router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed()
      )
      .subscribe(() => this.hideMenu());
  }

  ngOnDestroy(): void {
    this.menuOutsideClickListener?.();
  }

  private hideMenu(): void {
    this.layoutService.closeMenuOverlays();
    this.menuOutsideClickListener?.();
    this.menuOutsideClickListener = undefined;
  }
}
